<script setup lang="ts">
import AppTable from "@/components/app-table.vue";
import type { ContractDTO } from "@/models/dtos/contract-dto";
import type { FileDTO } from "@/models/dtos/file-dto";
import type { NotesDTO } from "@/models/dtos/notes-dto";
import type { ServiceOrderDTO } from "@/models/dtos/service-order-dto";
import { TableColumn } from "@/models/table-column";
import { RouteName } from "@/router";
import { contractsService } from "@/services/contracts-service";
import { authService } from "@/services/auth-service";
import { notificationsService } from "@/services/notifications-service";
import { serviceOrdersService } from "@/services/service-orders-service";
import { formatDate } from "@/utils/date-utils";
import { isEqual, isString } from "lodash-es";
import { computed, onBeforeMount, ref } from "vue";
import { onBeforeRouteLeave, useRoute, useRouter } from "vue-router";
import type { CustomerDTO } from "@/models/dtos/customer-dto";
import {
  isOneAdmin,
  isOneUser,
  isOneUserAdmin,
  userCustomerIds,
} from "@/utils/roles.util";
import { customersService } from "@/services/customers-service";

interface FilterFormData {
  selectedCustomerId?: string;
  selectedContractId?: number;
  returnArchived: boolean;
  returnInactive: boolean;
  returnAvrapportering: boolean;
  returnFinished: boolean;
}

interface CacheKeys {
  searchResult: string;
  customers: string;
  contracts: string;
  formData: string;
  filter: string;
  sort: string;
}

const cacheKeys = ref<CacheKeys>({
  searchResult: "searchResult",
  customers: "customers",
  contracts: "contracts",
  formData: "formData",
  filter: "filter",
  sort: "sort",
});

const router = useRouter();
const activeRoute = useRoute();

const pageLoading = ref(false);
const loading = ref(false);
const filter = ref();

const sort = computed(() => {
  const sorted = sessionStorage.getItem(cacheKeys.value.sort);
  if (sorted) {
    return JSON.parse(sorted);
  }

  return {
    key: null as string | null,
    asc: true,
  };
});

const columns = ref([
  TableColumn.for("id", "Ärende ID"),
  TableColumn.for("title", "Rubrik"),
  TableColumn.for("facilityName", "Anläggning"),
  TableColumn.for("address", "Address"),
  TableColumn.for("postAddress", "Postadress"),
  TableColumn.for("status", "Status"),
  TableColumn.for("statusTime", "Status from", {
    formatter: formatDate,
  }),
  TableColumn.for("created", "Skapad", {
    formatter: formatDate,
  }),
  TableColumn.for("files", "Filer", {
    formatter: (files?: FileDTO[]) => files?.length ?? "",
  }),
  TableColumn.for("notes", "Noteringar", {
    formatter: (notes?: NotesDTO[]) => notes?.length ?? "",
  }),
]);

const formData = ref<FilterFormData>({
  selectedCustomerId: undefined,
  selectedContractId: undefined,
  returnArchived: false,
  returnInactive: false,
  returnAvrapportering: true,
  returnFinished: false,
});

const customers = ref<CustomerDTO[]>([]);
const contracts = ref<ContractDTO[]>([]);
const serviceOrders = ref<ServiceOrderDTO[]>([]);

onBeforeMount(async () => {
  pageLoading.value = true;

  await loadCustomers();
  await loadContracts();
  await loadServiceOrdersForSelectedContract();

  pageLoading.value = false;
});

onBeforeRouteLeave((to, _from, next) => {
  if (!isEqual(to.name, RouteName.ServiceOrder)) {
    clearAllCache();
  }
  next();
});

async function loadCustomers() {
  try {
    const loggedInUser = authService.loggedInUser;
    const cachedCustomers = sessionStorage.getItem(cacheKeys.value.customers);
    if (cachedCustomers) {
      customers.value = JSON.parse(cachedCustomers);
    } else {
      if (!isOneAdmin(loggedInUser)) {
        const t = userCustomerIds(loggedInUser);
        customers.value = (await customersService.getCustomers()).filter(
          (customer) => t.includes(customer.customerNumber),
        );
      } else {
        customers.value = await customersService.getCustomers();
      }
      sessionStorage.setItem(
        cacheKeys.value.customers,
        JSON.stringify(customers.value),
      );
    }
    formData.value.selectedCustomerId = customers.value?.[0]?.customerNumber;
  } catch (error) {
    notificationsService.open({
      text: "Något gick fel vid hämtning av affärerna.",
      type: "error",
      error,
    });
  }
}

async function loadContracts() {
  try {
    const loggedInUser = authService.loggedInUser;

    const query: Record<string, unknown> = {};
    query.customerNumber = formData.value.selectedCustomerId;

    const isOneRole =
      isOneAdmin(loggedInUser) ||
      isOneUser(loggedInUser) ||
      isOneUserAdmin(loggedInUser);
    if (!isOneRole && loggedInUser?.id) {
      query.includeUserId = loggedInUser?.id;
    }

    const cachedContracts = sessionStorage.getItem(cacheKeys.value.contracts);
    if (cachedContracts) {
      contracts.value = JSON.parse(cachedContracts);
    } else if (isOneUser(loggedInUser)) {
      // Special case for one_user and one_user_admin
      let queryContractId: undefined | number;
      if (isString(activeRoute.query?.contractId)) {
        queryContractId = Number(activeRoute.query?.contractId);
      }
      formData.value.selectedContractId =
        queryContractId ?? contracts.value?.[0]?.id;
      if (formData.value.selectedContractId != undefined) {
        const selectedContractId = formData.value.selectedContractId.toString();
        contracts.value = [
          await contractsService.getContract(selectedContractId),
        ];
      }
    } else {
      contracts.value = await contractsService.getContracts(query);
      sessionStorage.setItem(
        cacheKeys.value.contracts,
        JSON.stringify(contracts.value),
      );
    }

    let queryContractId: undefined | number;
    if (isString(activeRoute.query?.contractId)) {
      queryContractId = Number(activeRoute.query?.contractId);
    }
    formData.value.selectedContractId =
      queryContractId ?? contracts.value?.[0]?.id;
  } catch (error) {
    notificationsService.open({
      text: "Något gick fel vid hämtning av affärerna.",
      type: "error",
      error,
    });
  }
}

async function loadServiceOrdersForSelectedContract(forceReload = false) {
  try {
    const cachedFormData = sessionStorage.getItem(cacheKeys.value.formData);
    if (cachedFormData && !forceReload) {
      formData.value = JSON.parse(cachedFormData);
    } else {
      sessionStorage.setItem(
        cacheKeys.value.formData,
        JSON.stringify(formData.value),
      );
    }

    const {
      selectedCustomerId,
      selectedContractId,
      returnArchived,
      returnInactive,
      returnAvrapportering,
    } = formData.value;

    if (!selectedCustomerId) {
      formData.value.selectedCustomerId = selectedCustomerId;
    }

    if (!selectedContractId) return;

    const query: Record<string, unknown> = {
      customerId: selectedCustomerId,
      contractId: selectedContractId,
      returnArchived,
      returnInactive,
      returnAvrapportering,
    };

    if (query.customerId === "100000") {
      // Special case for one_user and one_user_admin
      query.customerId = undefined;
    }

    const storedSearchResult = sessionStorage.getItem(
      cacheKeys.value.searchResult,
    );

    if (!forceReload && storedSearchResult && storedSearchResult.length > 0) {
      serviceOrders.value = JSON.parse(storedSearchResult);
    } else {
      serviceOrders.value = await serviceOrdersService.getServiceOrders(query);

      sessionStorage.setItem(
        cacheKeys.value.searchResult,
        JSON.stringify(serviceOrders.value),
      );
    }

    if (!forceReload) {
      const cachedFilter = sessionStorage.getItem(cacheKeys.value.filter);
      if (cachedFilter) {
        filter.value = cachedFilter;
      }
    }
  } catch (error) {
    notificationsService.open({
      text: "Något gick fel vid hämtning av serviceärenden.",
      type: "error",
      error,
    });
  }
}

function clearSearchResultCache() {
  sessionStorage.removeItem(cacheKeys.value.searchResult);
  sessionStorage.removeItem(cacheKeys.value.formData);
  sessionStorage.removeItem(cacheKeys.value.filter);
  sessionStorage.removeItem(cacheKeys.value.sort);
}

function clearAllCache() {
  sessionStorage.removeItem(cacheKeys.value.customers);
  sessionStorage.removeItem(cacheKeys.value.contracts);
  sessionStorage.removeItem(cacheKeys.value.searchResult);
  sessionStorage.removeItem(cacheKeys.value.formData);
  sessionStorage.removeItem(cacheKeys.value.filter);
  sessionStorage.removeItem(cacheKeys.value.sort);
}

const selectedContract = computed(() =>
  contracts.value.find((c) => c.id === formData.value?.selectedContractId),
);

const hasContracts = computed(() => contracts.value.length > 0);
const hasCustomers = computed(() => customers.value.length > 0);

async function onChangeCustomer() {
  loading.value = true;

  clearAllCache();
  await router.replace({ query: undefined });
  await loadContracts();
  await loadServiceOrdersForSelectedContract();

  loading.value = false;
}

async function onChangeContract() {
  loading.value = true;

  clearSearchResultCache();
  await router.replace({ query: { contractId: selectedContract.value?.id } });
  await loadServiceOrdersForSelectedContract();

  loading.value = false;
}

async function onChangeStatusFlag() {
  loading.value = true;
  clearSearchResultCache();
  formData.value.returnFinished = false;
  await loadServiceOrdersForSelectedContract();
  loading.value = false;
}

async function onChangeOnlyFinished() {
  loading.value = true;
  if (formData.value.returnFinished) {
    serviceOrders.value = serviceOrders.value.filter((v) =>
      JSON.stringify(v).includes("Avslutad"),
    );
  } else {
    await loadServiceOrdersForSelectedContract();
  }
  loading.value = false;
}

function onClickRow(
  serviceOrder: ServiceOrderDTO,
  filter: string,
  sort: Record<string, unknown>,
) {
  sessionStorage.setItem(cacheKeys.value.filter, filter);
  sessionStorage.setItem(cacheKeys.value.sort, JSON.stringify(sort));
  router.push({
    name: RouteName.ServiceOrder,
    params: {
      serviceOrderId: serviceOrder.id,
    },
  });
}

async function onRefreshList() {
  loading.value = true;

  clearSearchResultCache();
  await loadServiceOrdersForSelectedContract(true);

  loading.value = false;
}
</script>

<template>
  <section class="section px-0">
    <template
      v-if="
        !pageLoading && hasContracts && !isOneUser(authService.loggedInUser)
      "
    >
      <p class="title is-4">Serviceärenden</p>
    </template>
    <section v-else-if="!pageLoading" class="section">
      <p class="subtitle is-4">
        Serviceärenden för {{ selectedContract?.name }}
      </p>
    </section>

    <app-loader v-if="pageLoading"></app-loader>

    <template v-if="!pageLoading && hasContracts && hasCustomers">
      <div class="columns is-justify-content-space-between">
        <div class="column">
          <div v-if="!isOneUser(authService.loggedInUser)" class="field">
            <label class="label" for="selectedContractId">Kund</label>
            <div class="control">
              <div class="select">
                <select
                  id="selectedCustomerId"
                  v-model="formData.selectedCustomerId"
                  name="selectedCustomerId"
                  @change="onChangeCustomer"
                >
                  <option
                    v-for="customer in customers"
                    :key="customer.customerNumber"
                    :value="customer.customerNumber"
                  >
                    {{ customer.name }} ({{ customer.customerNumber }})
                  </option>
                </select>
              </div>
            </div>
          </div>
          <div v-if="!isOneUser(authService.loggedInUser)" class="field">
            <label class="label" for="selectedContractId">Affär</label>
            <div class="control">
              <div class="select">
                <select
                  id="selectedContractId"
                  v-model="formData.selectedContractId"
                  name="selectedContractId"
                  @change="onChangeContract"
                >
                  <option
                    v-for="contract in contracts"
                    :key="contract.id"
                    :value="contract.id"
                  >
                    {{ contract.name }} ({{ contract.eppsusId }})
                  </option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="columns is-vcentered">
        <div class="column is-narrow">
          <div class="field">
            <div class="control">
              <label class="checkbox" for="showInactive">
                <input
                  id="showInactive"
                  v-model="formData.returnInactive"
                  type="checkbox"
                  @change="onChangeStatusFlag"
                />
                Visa makulerade
              </label>
            </div>
          </div>
        </div>

        <div class="column is-narrow">
          <div class="field">
            <div class="control">
              <label class="checkbox" for="showArchived">
                <input
                  id="showArchived"
                  v-model="formData.returnArchived"
                  type="checkbox"
                  @change="onChangeStatusFlag"
                />
                Visa arkiverade
              </label>
            </div>
          </div>
        </div>

        <div class="column is-narrow">
          <div class="field">
            <div class="control">
              <label class="checkbox" for="showAvrapportering">
                <input
                  id="showAvrapportering"
                  v-model="formData.returnAvrapportering"
                  type="checkbox"
                  @change="onChangeStatusFlag"
                />
                Visa avrapporteringsärenden
              </label>
            </div>
          </div>
        </div>

        <div class="column is-narrow">
          <div class="field">
            <div class="control">
              <label class="checkbox" for="showFinished">
                <input
                  id="showFinished"
                  v-model="formData.returnFinished"
                  type="checkbox"
                  @change="onChangeOnlyFinished"
                />
                Visa endast Avslutade
              </label>
            </div>
          </div>
        </div>

        <div
          v-if="formData.selectedContractId"
          class="column is-flex is-justify-content-flex-end"
        >
          <button
            class="button mr-2"
            :class="{ 'is-loading': loading }"
            @click="onRefreshList"
          >
            Uppdatera lista
          </button>
          <router-link
            :to="{
              name: RouteName.ServiceOrdersCreate,
              params: { contractId: formData.selectedContractId },
              query: activeRoute.query,
            }"
            class="button"
            >Skapa serviceärende</router-link
          >
        </div>
      </div>
    </template>
  </section>

  <section
    v-if="!pageLoading && hasCustomers && formData.selectedContractId"
    class="section notification has-background-aero-light"
  >
    <app-table
      :items="serviceOrders"
      :columns="columns"
      :loading="loading"
      export-csv
      :search-filter="filter"
      :sort-column="sort"
      @click:row="onClickRow"
    >
    </app-table>
  </section>
  <section v-else-if="!pageLoading" class="section">
    <p class="subtitle is-5">
      De finns inga kontrakt att hämta serviceärende för. Kontakta din lokala
      administratör för hjälp. hasContracts: {{ hasContracts }}, hasCustomers:
      {{ hasCustomers }}, formData.selectedContractId:
      {{ formData.selectedContractId }}
    </p>
  </section>
</template>
